/**
 * One loading placeholder per screen, shaped like that screen.
 *
 * Every lazy route used to share a single generic placeholder, so a dashboard
 * of cards and a two-column detail view both showed a header, four KPI cards
 * and a table. The page then replaced it with something of a different shape
 * and the layout jumped.
 *
 * Each export below is the spine of its own page: the same regions, in the
 * same order, at roughly the same sizes. They are deliberately thin — numbers
 * passed to the shared pieces — so keeping one honest is a one-line edit when
 * its page changes.
 */
import {
  Skeleton,
  SkeletonCardGrid,
  SkeletonDetailList,
  SkeletonFilterBar,
  SkeletonForm,
  SkeletonKpiRow,
  SkeletonListPage,
  SkeletonPageHeader,
  SkeletonPageShell,
  SkeletonSideNav,
  SkeletonSplit,
  SkeletonTable,
  SkeletonTabs,
} from '@/ui';
import importStyles from '@/features/import/ImportPage.module.css';
import { Suspense } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { RouteSkeleton } from './RouteFallback';

/**
 * One route's own Suspense boundary, with the placeholder shaped like that
 * route's page.
 *
 * A component rather than a helper function so the route table reads as
 * markup, and so this file exports only components.
 */
export function LazyRoute({ skeleton, children }: {
  skeleton: ReactElement;
  children: ReactNode;
}) {
  return <Suspense fallback={<RouteSkeleton>{skeleton}</RouteSkeleton>}>{children}</Suspense>;
}

/** Header, KPI strip, two analytics panels, then the quick-action tiles. */
export function DashboardSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={2} />
      <SkeletonKpiRow cards={4} />
      <SkeletonCardGrid cards={2} lines={5} />
      <SkeletonCardGrid cards={4} lines={2} />
    </SkeletonPageShell>
  );
}

/** Header, the source tabs, the drop zone & required columns card, configure inputs, then the imported history rows. */
export function ImportSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} withSubtitle={true} />
      <div className={importStyles.importCard}>
        {/* Tab chips row */}
        <div className={importStyles.segmentHeaderRow}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Skeleton width={85} height={34} radius="var(--radius-full)" />
            <Skeleton width={130} height={34} radius="var(--radius-full)" />
            <Skeleton width={120} height={34} radius="var(--radius-full)" />
            <Skeleton width={135} height={34} radius="var(--radius-full)" />
          </div>
          <Skeleton width={145} height={34} radius="var(--radius-full)" />
        </div>

        {/* Dropzone & Required columns grid */}
        <div className={importStyles.importTopGrid}>
          <div
            style={{
              height: 190,
              border: '2px dashed var(--border-light)',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: 20,
              backgroundColor: 'var(--bg-subtle)',
            }}
          >
            <Skeleton width={42} height={42} radius="8px" />
            <Skeleton width={240} height={14} radius="4px" />
            <Skeleton width={180} height={11} radius="4px" />
            <Skeleton width={110} height={32} radius="var(--radius-full)" />
          </div>
          <div
            style={{
              height: 190,
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-lg)',
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              backgroundColor: 'var(--bg-card)',
            }}
          >
            <Skeleton width={130} height={14} radius="4px" />
            <Skeleton width={220} height={11} radius="4px" />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Skeleton width={70} height={24} radius="var(--radius-full)" />
              <Skeleton width={85} height={24} radius="var(--radius-full)" />
              <Skeleton width={95} height={24} radius="var(--radius-full)" />
              <Skeleton width={90} height={24} radius="var(--radius-full)" />
            </div>
            <Skeleton width="100%" height={38} radius="var(--radius-md)" />
          </div>
        </div>

        {/* Configure grid: project name, add to existing, and auto-enrich card */}
        <div className={importStyles.configureGrid}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton width={90} height={12} radius="4px" />
            <Skeleton width="100%" height={38} radius="var(--radius-md)" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton width={140} height={12} radius="4px" />
            <Skeleton width="100%" height={38} radius="var(--radius-md)" />
          </div>
          <div
            style={{
              height: 58,
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-lg)',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              backgroundColor: 'var(--bg-card)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <Skeleton width={20} height={20} radius="4px" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                <Skeleton width={150} height={12} radius="4px" />
                <Skeleton width={220} height={10} radius="4px" />
              </div>
            </div>
            <Skeleton width={38} height={22} radius="11px" />
          </div>
        </div>

        {/* Footer bar */}
        <div className={importStyles.importFooterBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Skeleton width={14} height={14} radius="50%" />
            <Skeleton width={160} height={13} radius="4px" />
          </div>
          <Skeleton width={120} height={38} radius="var(--radius-full)" />
        </div>
      </div>

      {/* History section */}
      <div className={importStyles.historyContainer} style={{ marginTop: 24 }}>
        <div className={importStyles.historyHeader}>
          <Skeleton width={140} height={20} radius="4px" />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Skeleton width={160} height={34} radius="var(--radius-md)" />
            <Skeleton width={120} height={34} radius="var(--radius-md)" />
            <div style={{ display: 'flex', gap: 6 }}>
              <Skeleton width={50} height={30} radius="var(--radius-full)" />
              <Skeleton width={60} height={30} radius="var(--radius-full)" />
              <Skeleton width={60} height={30} radius="var(--radius-full)" />
              <Skeleton width={75} height={30} radius="var(--radius-full)" />
            </div>
          </div>
        </div>

        {/* History rows list */}
        <div className={importStyles.historyList}>
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className={importStyles.projectRow}>
              <div className={importStyles.rowMain}>
                <div className={importStyles.rowLeft}>
                  <div style={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Skeleton width={12} height={12} radius="4px" />
                  </div>
                  <div className={importStyles.rowContent}>
                    <div className={importStyles.rowTitleLine} style={{ gap: 10, alignItems: 'center' }}>
                      <Skeleton width={180 + (i % 3) * 40} height={15} radius="4px" />
                      <Skeleton width={75} height={20} radius="var(--radius-full)" />
                      <Skeleton width={45} height={12} radius="4px" />
                    </div>
                    <div className={importStyles.rowSubtitle} style={{ gap: 12, alignItems: 'center' }}>
                      <Skeleton width={60} height={12} radius="4px" />
                      <Skeleton width={110} height={12} radius="4px" />
                      <Skeleton width={90} height={12} radius="4px" />
                      <Skeleton width={80} height={12} radius="4px" />
                    </div>
                  </div>
                </div>
                <div className={importStyles.rowRight} style={{ gap: 8 }}>
                  <Skeleton width={100} height={28} radius="var(--radius-full)" />
                  <Skeleton width={110} height={28} radius="var(--radius-full)" />
                  <Skeleton width={110} height={28} radius="var(--radius-full)" />
                  <Skeleton width={110} height={28} radius="var(--radius-full)" />
                  <Skeleton width={85} height={28} radius="var(--radius-full)" />
                  <Skeleton width={28} height={28} radius="6px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SkeletonPageShell>
  );
}

/** Header, KPI strip, the category chips, then the project cards or table. */
export function ProjectsSkeleton() {
  const isGrid = (() => {
    try {
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const urlView = urlParams.get('view');
        if (urlView === 'grid') return true;
        if (urlView === 'table') return false;
        return window.localStorage?.getItem('leadgen_projects_view_mode') === 'grid';
      }
    } catch {
      // ignore storage errors
    }
    return false;
  })();

  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={2} />
      <SkeletonKpiRow cards={4} />
      <SkeletonFilterBar filters={4} />
      {isGrid ? (
        <SkeletonCardGrid cards={6} lines={3} />
      ) : (
        <SkeletonTable columns={8} rows={10} />
      )}
    </SkeletonPageShell>
  );
}

/** Header, the project's own tabs, its summary pairs, then its records. */
export function ProjectDetailSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={2} />
      <SkeletonTabs tabs={3} />
      <SkeletonDetailList rows={6} />
      <SkeletonTable columns={7} rows={8} />
    </SkeletonPageShell>
  );
}

/** Five stage cards, the active/inactive scope tabs, then a wide table. */
export function PeopleSkeleton() {
  return <SkeletonListPage headerActions={2} kpis={5} tabs={3} filters={2} columns={9} rows={10} />;
}

/** Tabs, the six bucket chips, the scope tabs, then a wide table. */
export function OrganisationsSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} />
      <SkeletonTabs tabs={3} />
      <SkeletonFilterBar filters={6} withSearch={false} />
      <SkeletonTabs tabs={3} />
      <SkeletonFilterBar filters={2} />
      <SkeletonTable columns={9} rows={10} />
    </SkeletonPageShell>
  );
}

/** Two sub-tabs, the campaign picker and filters, then the profiles table. */
export function CampaignsSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} />
      <SkeletonTabs tabs={2} />
      <SkeletonFilterBar filters={5} />
      <SkeletonTable columns={7} rows={10} />
    </SkeletonPageShell>
  );
}

/** Tabs, the schedules card, the five funnel cards, filters, then the queue. */
export function JobOpportunitiesSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={2} />
      <SkeletonTabs tabs={3} />
      <SkeletonCardGrid cards={1} lines={3} />
      <SkeletonKpiRow cards={5} />
      <SkeletonFilterBar filters={5} />
      <SkeletonTable columns={10} rows={10} />
    </SkeletonPageShell>
  );
}

/** The decision banner, then the two-column detail: cards left, panels right. */
export function OpportunityDetailSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={2} />
      <SkeletonCardGrid cards={1} lines={2} />
      <SkeletonSplit>
        <SkeletonCardGrid cards={4} lines={4} />
        <SkeletonCardGrid cards={4} lines={3} />
      </SkeletonSplit>
    </SkeletonPageShell>
  );
}

/** The company's stakeholder counts, then its people. */
export function CompanyPeopleSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} />
      <SkeletonKpiRow cards={4} />
      <SkeletonTable columns={7} rows={8} />
    </SkeletonPageShell>
  );
}

/** A short page: header, then the export cards. */
export function ExportsSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} />
      <SkeletonCardGrid cards={4} lines={3} />
    </SkeletonPageShell>
  );
}

/** Header, then the account form. No table, no KPIs. */
export function ProfileSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} withSubtitle={false} />
      <SkeletonForm fields={6} />
    </SkeletonPageShell>
  );
}

/** A side rail of sections, with the chosen section's table beside it. */
export function MasterDataSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} />
      <SkeletonSplit>
        <SkeletonSideNav groups={3} itemsPerGroup={4} />
        <SkeletonTable columns={5} rows={10} />
      </SkeletonSplit>
    </SkeletonPageShell>
  );
}

/** The settings rail — four groups — and the open group's table. */
export function SettingsSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={0} />
      <SkeletonSplit>
        <SkeletonSideNav groups={4} itemsPerGroup={3} />
        <SkeletonTable columns={2} rows={9} />
      </SkeletonSplit>
    </SkeletonPageShell>
  );
}

/** Review counts, the queue filters, then the organisations awaiting review. */
export function OrgReviewSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} />
      <SkeletonKpiRow cards={4} />
      <SkeletonFilterBar filters={4} />
      <SkeletonTable columns={8} rows={10} />
    </SkeletonPageShell>
  );
}

/** Role chips, then the user table. */
export function UsersSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={2} />
      <SkeletonKpiRow cards={4} />
      <SkeletonFilterBar filters={4} />
      <SkeletonTable columns={6} rows={10} />
    </SkeletonPageShell>
  );
}

/** Contact-type selector, then one country x channel grid per region. */
export function ComplianceApprovalsSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} />
      <SkeletonFilterBar filters={1} />
      <SkeletonTable columns={7} rows={10} />
    </SkeletonPageShell>
  );
}

/** The decision history table. */
export function ComplianceHistorySkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} />
      <SkeletonTable columns={9} rows={10} />
    </SkeletonPageShell>
  );
}

/** Header, the five section tabs, then the open tab's cards. */
export function OutreachSkeleton() {
  return (
    <SkeletonPageShell>
      <SkeletonPageHeader actions={1} />
      <SkeletonTabs tabs={5} />
      <SkeletonCardGrid cards={3} lines={4} />
    </SkeletonPageShell>
  );
}
